import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

export default function Disclaimer() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center gap-3">
          <AlertTriangle className="h-8 w-8 text-primary-500" />
          <h1 className="text-3xl font-bold text-white">Disclaimer</h1>
        </div>
        
        <div className="bg-zinc-900 rounded-lg p-8 mb-8">
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300">Last Updated: July 1, 2025</p>
            
            <div className="bg-primary-900/30 border border-primary-800 rounded-lg p-4 my-6">
              <h2 className="text-xl font-semibold text-primary-300 mb-2">IMPORTANT NOTICE</h2>
              <p className="text-primary-200">
                Motorsports activities are inherently dangerous. By using our facilities, you acknowledge and accept all risks associated with racing, track days, and motorsports events. Please read this disclaimer carefully.
              </p>
            </div>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">1. Assumption of Risk</h2>
            <p className="text-gray-300">
              Participation in motorsports activities at Jacky Circuit involves significant risks, including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li>Risk of serious bodily injury, including permanent disability, paralysis, and death</li>
              <li>Risk of damage to or loss of personal property</li>
              <li>Risks arising from surface and environmental conditions</li>
              <li>Risks arising from the actions or negligence of other participants</li>
              <li>Risks arising from mechanical failures or equipment malfunctions</li>
            </ul>
            <p className="text-gray-300 mt-2">
              By using our facilities or participating in our events, you voluntarily assume all such risks, both known and unknown, even if arising from the negligence of others.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">2. Release of Liability</h2>
            <p className="text-gray-300">
              To the fullest extent permitted by law, you hereby release, waive, and discharge Jacky Circuit, its owners, officers, directors, employees, agents, and affiliates from any and all liability, claims, demands, actions, and causes of action whatsoever arising out of or related to any loss, damage, or injury, including death, that may be sustained by you or your property while participating in or observing activities at our facilities.
            </p>
            <p className="text-gray-300 mt-2">
              This release extends to any claim based on the negligence, action, or inaction of Jacky Circuit, its staff, or other participants, and covers claims for personal injury, property damage, wrongful death, breach of contract, or any other type of suit.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">3. Safety Requirements</h2>
            <p className="text-gray-300">
              All participants must:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li>Wear appropriate safety gear at all times while on the track</li>
              <li>Ensure their vehicles meet all safety requirements</li>
              <li>Follow all track rules and staff instructions</li>
              <li>Refrain from participating while under the influence of alcohol or drugs</li>
              <li>Report any unsafe conditions to staff immediately</li>
            </ul>
            <p className="text-gray-300 mt-2">
              Jacky Circuit reserves the right to refuse track access to anyone who does not comply with safety requirements or whose behavior poses a risk to themselves or others.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">4. Medical Fitness</h2>
            <p className="text-gray-300">
              By participating in activities at Jacky Circuit, you represent that you are in good health and proper physical condition to safely participate in such activities. You agree that you will not participate if you have any medical condition that could be aggravated by physical activity or competition.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">5. Property Damage</h2>
            <p className="text-gray-300">
              You are responsible for any damage you cause to Curacao International Raceway facilities, equipment, or property. For certain bookings, a security deposit is required and will be used to cover any damages. If damages exceed the deposit amount, you will be responsible for the additional costs.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">6. Image and Likeness</h2>
            <p className="text-gray-300">
              By participating in activities at Jacky Circuit, you grant us permission to use photographs, videos, and recordings of you and your vehicle for promotional, educational, and commercial purposes without compensation.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">7. Website and Booking System</h2>
            <p className="text-gray-300">
              Our website and booking system are provided "as is" and "as available" without any warranties, expressed or implied. We do not guarantee that our website will be uninterrupted, timely, secure, or error-free.
            </p>
            <p className="text-gray-300 mt-2">
              While we strive to maintain accurate information regarding track availability, event schedules, and pricing, errors may occur. We reserve the right to correct any errors and to change or update information at any time without prior notice.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">8. Event Cancellation</h2>
            <p className="text-gray-300">
              Jacky Circuit reserves the right to cancel or reschedule events due to weather conditions, track maintenance, safety concerns, or other circumstances beyond our control. In case of cancellation by Jacky Circuit, participants will be offered a refund or the option to reschedule.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">9. Acknowledgment</h2>
            <p className="text-gray-300">
              By using our facilities, participating in our events, or using our booking system, you acknowledge that you have read this disclaimer, understand its contents, and agree to be bound by its terms. You further acknowledge that you are participating voluntarily and that all risks have been made clear to you.
            </p>
            
            <div className="bg-primary-900/30 border border-primary-800 rounded-lg p-4 my-6">
              <p className="text-primary-200 font-semibold">
                REMEMBER: Motorsports can be dangerous. Always prioritize safety, follow all rules and instructions, and drive within your limits.
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-center mb-8">
          <Link to="/" className="flex items-center gap-2 text-primary-500 hover:text-primary-400">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}